import type moengage from '@moengage/web-sdk';
import merge from 'deepmerge';
import type {
	AnalyticsCreateProjectProperties,
	AnalyticsEcommerceProduct,
	AnalyticsOpenProjectProperties,
	AnalyticsProjectReadyProperties,
	AnalyticsSubController,
	AnalyticsUserProperties,
	PricingObject,
} from 'interfaces/app';
import type * as DB from 'interfaces/database';
import {
	AppDataModule,
	AppStateModule,
	ConfigModule,
	ExternalUsersModule,
	ProductsModule,
	UserModule,
} from 'store';
import _ from 'underscore';

export default class MoengageController implements AnalyticsSubController {
	private sdk: Promise<typeof moengage | undefined> = Promise.resolve(undefined);

	private shownWebPushSoftAsk = false;

	private userProperties: AnalyticsUserProperties = {};

	public init() {
		if (
			ConfigModule['analytics.moengage.id']
			&& ConfigModule['analytics.moengage.enabled']
			&& window.moengageTrackerAvailable
		) {
			const setupProperties = {
				app_id: ConfigModule['analytics.moengage.id'],
				debug_logs: ConfigModule['analytics.moengage.debug'] ? 1 : 0,
				cluster: ConfigModule['analytics.moengage.cluster'],
				swPath: '/sw.js',
			};
			this.sdk = import('@moengage/web-sdk')
				.then((sdk) => {
					this.sdk = Promise.resolve(sdk);
					sdk.initialize(setupProperties);

					if (window.glPlatform === 'web') {
						AppStateModule.enableWebPushAvailability();
					} else if (window.glPlatform === 'native') {
						if (!window.webToNative) {
							throw new Error('Missing WebToNative on window');
						}

						// Note: Android app does not have this function yet
						window.webToNative
							.getPermissions()
							?.then((permissionDetails) => {
								if (!permissionDetails.pushEnabled) {
									window.webToNative?.requestPushPermission();
								}
							});
					}

					this.identifyUser();

					return sdk;
				});

			return true;
		}

		return false;
	}

	private logFailure() {
		if (typeof window.glBugsnagClient !== 'undefined') {
			window.glBugsnagClient.notify(
				new Error('Moengage Web SDK not loaded (possibly due to adblocker)'),
				(event) => {
					event.severity = 'info';
				},
			);
		}

		return undefined;
	}

	public setExperimentFlags(
		flags: Record<string, string | number | boolean | null>,
	) {
		this.sdk.then((sdk) => {
			if (!sdk?.isMoeLoaded()) {
				return this.logFailure();
			}

			Object.entries(flags).forEach(([
				flagName,
				flagValue,
			]) => {
				sdk.add_user_attribute(
					flagName,
					flagValue,
				);
			});

			return undefined;
		});
	}

	public setUserProperties(
		objProperties: AnalyticsUserProperties,
	) {
		this.sdk.then((sdk) => {
			if (!sdk?.isMoeLoaded()) {
				return this.logFailure();
			}

			this.userProperties = merge(
				this.userProperties,
				objProperties,
			);

			_.pairs(objProperties).forEach((pair) => {
				const [
					k,
					v,
				] = pair;

				if (k == 'First_name') {
					sdk.add_first_name(v);
				} else if (k == 'Last_name') {
					sdk.add_last_name(v);
				} else if (k == 'Email') {
					sdk.add_email(v);
				} else {
					sdk.add_user_attribute(
						k,
						v,
					);
				}
			});

			return undefined;
		});
	}

	public logout() {
		this.sdk.then((sdk) => {
			if (!sdk?.isMoeLoaded()) {
				return this.logFailure();
			}

			sdk.destroy_session();

			return undefined;
		});
	}

	private track(
		event: string,
		properties: Record<string, any>,
	) {
		this.sdk.then((sdk) => {
			if (!sdk?.isMoeLoaded()) {
				return this.logFailure();
			}

			sdk.track_event(
				event,
				properties,
			);

			return undefined;
		});
	}

	public aliasUser(
		oldUserId: DB.UserModel['id'],
		newUserId: DB.UserModel['id'],
	) {
		this.sdk.then((sdk) => {
			if (!sdk?.isMoeLoaded()) {
				return this.logFailure();
			}

			if (newUserId) {
				sdk.update_unique_user_id(
					newUserId.toString(),
				);
			}

			return undefined;
		});
	}

	public registerUser() {
		this.identifyUser();
	}

	public identifyUser() {
		this.sdk.then((sdk) => {
			if (!sdk?.isMoeLoaded()) {
				return this.logFailure();
			}

			if (UserModule.id) {
				if (ConfigModule['analytics.moengage.externalUserId']) {
					const externalUser = ExternalUsersModule.findWhere({
						source: 'app',
					});

					if (
						externalUser
						&& externalUser.externalId
					) {
						sdk.add_unique_user_id(
							externalUser.externalId,
						);
					}

					sdk.add_user_attribute(
						'FastEditor_Id',
						UserModule.id,
					);
				} else {
					sdk.add_unique_user_id(
						UserModule.id.toString(),
					);
				}

				if (
					ConfigModule['features.webPush']
					&& !this.shownWebPushSoftAsk
					&& window.glPlatform == 'web'
					&& (
						UserModule.session_nr === 1
						|| UserModule.session_nr % 10 === 0
					)
				) {
					sdk.call_web_push({
						soft_ask: true,
						main_class: 'webPushSoftAsk',
						allow_class: 'acceptButton',
						block_class: 'declineButton',
					});
					this.shownWebPushSoftAsk = true;
				}
			}

			return undefined;
		});
	}

	public trackPageView() {
		this.sdk.then((sdk) => {
			if (!sdk?.isMoeLoaded()) {
				return this.logFailure();
			}

			sdk.track_page_view();

			return undefined;
		});
	}

	public trackEvent(
		action: string,
		objProperties: Record<string, any>,
	) {
		this.track(
			action,
			objProperties,
		);
	}

	public trackProductReady(
		productModel: DB.ProductModel | null,
		objPrice: PricingObject | null,
		objProperties: AnalyticsProjectReadyProperties,
	) {
		const properties = {
			page_count: objProperties.pageCount,
			photo_count: objProperties.photoCount,
			groupid: objProperties.groupid,
			typeid: objProperties.typeid,
			variantid: objProperties.variantid,
			product_origin: objProperties.product_origin,
			type: objProperties.type,
			price: objPrice ? Math.round(objPrice.subTotal) / 100 : undefined,
			theme_id: productModel ? productModel.themeid : undefined,
			layout_id: productModel ? productModel.layoutid : undefined,
		};

		this.track(
			'Product ready',
			properties,
		);
	}

	public trackAddToCart(
		cartItemModel: DB.ShoppingCartItemModel,
		objPrice: PricingObject,
		objProperties: Record<string, any>,
	) {
		const offeringModel = AppDataModule.getOffering(cartItemModel.offeringid);

		if (offeringModel) {
			const productModel = ProductsModule.getById(cartItemModel.productid);

			const baseUrl = `${window.location.protocol}//${window.location.host}`;
			const productUrl = `${baseUrl}/app/${cartItemModel.productid}/open`;

			this.track(
				'Product Added',
				{
					...objProperties,
					// Semantic properties
					cart_id: cartItemModel.cartid,
					product_id: cartItemModel.productid,
					sku: offeringModel.id,
					category: AppDataModule.getProductCategoryName(offeringModel.groupid),
					name: AppDataModule.getOfferingName(offeringModel.id),
					variant: offeringModel.variantid,
					price: Math.round(objPrice.subTotal) / 100,
					quantity: cartItemModel.quantity,
					url: productUrl,
					// Custom properties
					groupid: cartItemModel.groupid,
					product_origin: productModel
						? productModel.origin
						: undefined,
				},
			);
		}
	}

	public trackRemoveFromCart(
		cartItemModel: DB.ShoppingCartItemModel,
		objProperties: Record<string, any>,
	) {
		const offeringModel = AppDataModule.getOffering(cartItemModel.offeringid);

		if (offeringModel) {
			this.track(
				'Removed Product',
				{
					...objProperties,
					cart_id: cartItemModel.cartid,
					product_id: cartItemModel.productid,
					sku: offeringModel.id,
					category: AppDataModule.getProductCategoryName(offeringModel.groupid),
					name: AppDataModule.getOfferingName(offeringModel.id),
					variant: offeringModel.variantid,
					quantity: cartItemModel.quantity,
				},
			);
		}
	}

	public trackPerformance(
		action: string,
		value: number,
		objProperties: Record<string, any>,
	) {
		this.track(
			`Performance: ${action}`,
			{
				...objProperties,
				performanceValue: value,
			},
		);
	}

	public trackCreateProject(
		productModel: DB.ProductModel,
		objProperties: AnalyticsCreateProjectProperties,
	) {
		this.track(
			'Create Product',
			objProperties,
		);
	}

	public trackOpenProduct(
		productModel: DB.ProductModel,
		objProperties: AnalyticsOpenProjectProperties,
	) {
		this.track(
			'Open Product',
			objProperties,
		);
	}

	public trackTransaction(
		orderModel: DB.OrderModel,
		orderItems: DB.OrderItemModel[],
		objProperties: Record<string, any>,
	) {
		const arrProducts: AnalyticsEcommerceProduct[] = [];
		orderItems.forEach((orderItemModel) => {
			const offeringModel = AppDataModule.getOffering(orderItemModel.offeringid);

			if (offeringModel) {
				arrProducts.push({
					...objProperties,
					product_id: orderItemModel.productid.toString(),
					sku: offeringModel.id.toString(),
					category: AppDataModule.getProductCategoryName(offeringModel.groupid),
					name: AppDataModule.getOfferingName(offeringModel.id),
					variant: offeringModel.variantid.toString(),
					price: Math.round(orderItemModel.salesvalue) / 100,
					quantity: orderItemModel.quantity,
				});
			}
		});

		this.track(
			'Order Completed',
			{
				...objProperties,
				order_id: orderModel.id,
				total: Math.round(orderModel.salesvalue) / 100,
				shipping: Math.round(orderModel.price_shipping) / 100,
				tax: Math.round(orderModel.total_tax) / 100,
				discount: Math.round(orderModel.discount_voucher) / 100,
				coupon: orderModel.voucherid ? `${orderModel.voucherid}` : null,
				currency: orderModel.currency,
				products: arrProducts,
			},
		);
	}
}
